import moment from 'moment';
import { Button, Modal, ModalHeader, ModalBody, ModalFooter } from 'reactstrap';
import React, { useState, useEffect, useRef, useMemo } from 'react';
import { getSlotName, timeSlots } from './slotOptions';

const PhysiotherapyModalConfirm = ({
    submitModal,
    onComplete,
    closeModal,
    modalIsOpen,
    className,
    requestId,
    requestedAppointmentDate,
}) => {
    // Calculate initial date value
    const getInitialDate = () => {
        if (requestedAppointmentDate) {
            return moment(requestedAppointmentDate).format('YYYYMMDD');
        }
        return moment().format('YYYYMMDD');
    };

    const [value, setValue] = useState(getInitialDate());
    const [slotValue, setSlotValue] = useState('');
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const dropdownRef = useRef(null);

    // Calculate minimum date (either requestedAppointmentDate or today, whichever is later)
    const minDate = useMemo(() => {
        if (!requestedAppointmentDate) {
            return moment().format('YYYY-MM-DD');
        }
        const requestedDate = moment(requestedAppointmentDate);
        const todayDate = moment();
        return requestedDate.isAfter(todayDate)
            ? requestedDate.format('YYYY-MM-DD')
            : todayDate.format('YYYY-MM-DD');
    }, [requestedAppointmentDate]);

    // Reset to default values when modal opens or requestId changes
    useEffect(() => {
        if (modalIsOpen) {
            // Set initial value to the requestedAppointmentDate (not today)
            const initialDate = requestedAppointmentDate
                ? moment(requestedAppointmentDate).format('YYYYMMDD')
                : moment().format('YYYYMMDD');
            setValue(initialDate);
            setSlotValue('');
            setIsDropdownOpen(false);
        }
    }, [modalIsOpen, requestId, requestedAppointmentDate]);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsDropdownOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    const handleChange = (event) => {
        const date = moment(event.target.value, 'YYYY-MM-DD').format('YYYYMMDD');
        setValue(date);
    };

    const handleSlotSelect = (slotVal) => {
        setSlotValue(slotVal);
        setIsDropdownOpen(false);
    };

    const toggleDropdown = () => {
        setIsDropdownOpen(!isDropdownOpen);
    };

    const getSelectedLabel = () => {
        const selected = timeSlots.find((slot) => slot.value === slotValue);
        return selected ? selected.label : 'Select Time';
    };

    const handleSubmit = () => {
        const slotName = getSlotName(slotValue);

        submitModal(
            moment(value, 'YYYYMMDD').format('YYYY-MM-DD'),
            slotValue,
            slotName
        ).then(() => {
            closeModal();
            onComplete();
        });
    };

    return (
        <Modal isOpen={modalIsOpen} toggle={closeModal} className={className}>
            <ModalHeader className="bg-primary" toggle={closeModal}>
                Confirm Request (Request Id: {requestId})
            </ModalHeader>
            <ModalBody>
                <form>
                    <label>Date:</label>
                    <br />
                    <input
                        type="date"
                        value={moment(value, 'YYYYMMDD').format('YYYY-MM-DD')}
                        min={minDate}
                        onChange={handleChange}
                        style={{
                            width: '100%',
                            padding: '8px',
                            marginBottom: '15px',
                            borderRadius: '4px',
                            border: '1px solid #ccc',
                        }}
                    />
                    <br />
                    <label>Select Slot:</label>
                    <br />
                    <div
                        ref={dropdownRef}
                        style={{ position: 'relative', width: '100%' }}
                    >
                        <div
                            onClick={toggleDropdown}
                            style={{
                                width: '100%',
                                padding: '8px',
                                borderRadius: '4px',
                                border: '1px solid #ccc',
                                cursor: 'pointer',
                                backgroundColor: 'white',
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                fontSize: '14px',
                                color: slotValue ? '#333' : '#999',
                            }}
                        >
                            <span>{getSelectedLabel()}</span>
                            <span
                                style={{
                                    fontSize: '12px',
                                    color: '#999',
                                    transform: isDropdownOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                                    transition: 'transform 0.2s',
                                }}
                            >
                                ▼
                            </span>
                        </div>
                        {isDropdownOpen && (
                            <div
                                style={{
                                    position: 'absolute',
                                    top: '100%',
                                    left: 0,
                                    right: 0,
                                    backgroundColor: 'white',
                                    border: '1px solid #ccc',
                                    borderRadius: '4px',
                                    marginTop: '4px',
                                    maxHeight: '250px',
                                    overflowY: 'auto',
                                    boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
                                    zIndex: 1000,
                                }}
                            >
                                {timeSlots.map((slot) => (
                                    <div
                                        key={slot.value}
                                        onClick={() => handleSlotSelect(slot.value)}
                                        style={{
                                            padding: '12px 16px',
                                            cursor: 'pointer',
                                            backgroundColor:
                                                slotValue === slot.value ? '#f0f0f0' : 'white',
                                            borderBottom: '1px solid #f0f0f0',
                                            fontSize: '16px',
                                            transition: 'background-color 0.2s',
                                        }}
                                        onMouseEnter={(e) => {
                                            if (slotValue !== slot.value) {
                                                e.target.style.backgroundColor = '#f8f8f8';
                                            }
                                        }}
                                        onMouseLeave={(e) => {
                                            if (slotValue !== slot.value) {
                                                e.target.style.backgroundColor = 'white';
                                            }
                                        }}
                                    >
                                        {slot.label}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </form>
            </ModalBody>
            <ModalFooter>
                <Button color="info" onClick={handleSubmit} disabled={!slotValue}>
                    Confirm
                </Button>
                <Button color="secondary" onClick={closeModal}>
                    Close
                </Button>
            </ModalFooter>
        </Modal>
    );
};

export default PhysiotherapyModalConfirm;
